import { useState } from "react";
import { toast } from "react-toastify";
import { resubmitPost } from "../../services/api";
import * as Sentry from "@sentry/react";
import { CommunityError } from "../../errors/sentryErrors";

const ResubmitFlow = ({ isOpen, onClose, post, onResubmitSuccess }) => {
  const [isResubmitting, setIsResubmitting] = useState(false);

  if (!isOpen || !post) return null;

  const handleResubmit = async () => {
    try {
      setIsResubmitting(true);
      await resubmitPost(post.id);
      toast.success("Post resubmitted successfully!");
      if (onResubmitSuccess) {
        onResubmitSuccess();
      }
      onClose();
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
      });
      toast.error("Failed to resubmit: " + (err.message || err.error));
    } finally {
      setIsResubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-10">
          <h3 className="text-xl font-bold text-gray-900">Resubmit Post</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto">
          <div className="mb-6">
            <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-2">Why was it rejected?</h4>
            <div className="bg-red-50 border border-red-100 rounded-lg p-4">
              <p className="text-sm font-medium text-red-800 mb-1">
                Reason: {post.rejectionReason?.replace(/_/g, ' ').toUpperCase() || 'MODERATOR REVIEW'}
              </p>
              <p className="text-sm text-gray-700">
                {post.rejectionNote || "Please ensure your post meets our community guidelines."}
              </p>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-2">How to fix</h4>
            <ul className="text-sm text-gray-600 list-disc pl-5 space-y-2">
              <li>Review your trip details and itinerary content.</li>
              <li>Ensure your photos do not contain personal information.</li>
              <li>Check your review text for inappropriate language.</li>
              <li>Make changes by going to the trip details or editing your review.</li>
            </ul>
          </div>
          
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              Only click Resubmit if you have made the necessary changes. Repeatedly submitting unedited rejected posts may result in account restrictions.
            </p>
          </div>
        </div>

        <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 z-10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleResubmit}
            disabled={isResubmitting}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
          >
            {isResubmitting ? "Resubmitting..." : "Resubmit Now"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResubmitFlow;
